"use client";

import { useState } from "react";
import { DocumentDownload } from "./document-download.client";

type DocumentLink = {
  publicId: string;
  supplyKey: string;
  documentKind: string;
  documentNumber: string;
};
/** Documents are fetched on demand, keeping booking/order lists inexpensive. */
export function CommerceDocumentLinks({
  sourceType,
  sourceId,
}: {
  sourceType:
    | "event_booking"
    | "venue_booking"
    | "shop_order"
    | "pack_holding"
    | "membership";
  sourceId: number;
}) {
  const [documents, setDocuments] = useState<DocumentLink[] | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = async () => {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      const response = await fetch(
        `/api/commerce-documents?sourceType=${sourceType}&sourceId=${sourceId}`,
        { cache: "no-store" },
      );
      if (!response.ok) throw new Error("Unable to load documents. Try again.");
      const data: { documents: DocumentLink[] } = await response.json();
      setDocuments(data.documents);
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : "Unable to load documents.",
      );
    } finally {
      setPending(false);
    }
  };
  return (
    <div className="space-y-2 text-xs">
      {documents === null || error ? (
        <button
          type="button"
          className="font-medium text-brand underline underline-offset-2"
          disabled={pending}
          onClick={load}
        >
          {pending
            ? "Loading documents…"
            : "Receipts, invoices and credit notes"}
        </button>
      ) : documents.length === 0 ? (
        <p className="text-text-tertiary">
          Documents will appear after payment confirmation.
        </p>
      ) : (
        <ul className="space-y-2">
          {documents.map((document) => (
            <li key={document.publicId}>
              <span>
                {document.supplyKey === "platform" ||
                document.supplyKey.endsWith(":platform")
                  ? "Platform fee"
                  : "Seller"}{" "}
                · {document.documentKind.replaceAll("_", " ")} ·{" "}
                {document.documentNumber}
              </span>
              <div className="mt-1">
                <DocumentDownload
                  href={`/api/commerce-documents/${document.publicId}`}
                  documentNumber={document.documentNumber}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
      {error ? (
        <p role="status" className="text-text-tertiary">
          {error}
        </p>
      ) : null}
    </div>
  );
}
